import { useEffect } from "react";
import environment from "@/settings/environment";
import { useTranslation } from "react-i18next";

const useCaptchaEnterprise = ({
    setIsVerified,
    captchaName,
    action = 'submit',
    enabled = true,
}: {
    setIsVerified: (value: boolean) => void;
    captchaName: string;
    action?: string;
    enabled?: boolean;
}) => {
    const { i18n } = useTranslation();

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const currentLanguage = i18n.language || 'pt-BR';
        
        const existingScripts = document.querySelectorAll('script[src*="recaptcha/enterprise.js"]');
        existingScripts.forEach(script => script.remove());
        
        if ((window as any).grecaptcha) {
            delete (window as any).grecaptcha;
        }

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?hl=${currentLanguage}`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
            console.log('reCAPTCHA Enterprise script loaded with language:', currentLanguage);
        };
        
        script.onerror = () => {
            console.error('Failed to load reCAPTCHA Enterprise script');
        };

        document.head.appendChild(script);
    }, [i18n.language, enabled]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const checkRecaptchaToken = () => {
            const token = (window as any).getRecaptchaToken?.();
            if (token) {
                setIsVerified(true);
            } else {
                setIsVerified(false);
            }
        };

        checkRecaptchaToken();

        (window as any).onRecaptchaVerify = function(token: string) {
            (window as any).recaptchaToken = token;
            setIsVerified(true);
        };
        
        (window as any).onRecaptchaExpired = function() {
            (window as any).recaptchaToken = null;
            setIsVerified(false);
        };
        
        (window as any).onRecaptchaError = function() {
            (window as any).recaptchaToken = null;
            setIsVerified(false);
        };

        (window as any).getRecaptchaToken = function() {
            return (window as any).recaptchaToken || null;
        };

        (window as any).getRecaptchaAction = function() {
            return action;
        };

        const executeRecaptcha = () => {
            if ((window as any).grecaptcha?.enterprise) {
                (window as any).grecaptcha.enterprise.execute(environment.recaptcha.siteKey, {action})
                    .then((token: string) => {
                        (window as any).recaptchaToken = token;
                        setIsVerified(true);
                    })
                    .catch(() => {
                        setIsVerified(false);
                    });
            }
        };

        (window as any).executeRecaptcha = executeRecaptcha;

        return () => {
            (window as any).onRecaptchaVerify = undefined;
            (window as any).onRecaptchaExpired = undefined;
            (window as any).onRecaptchaError = undefined;
            (window as any).getRecaptchaToken = undefined;
            (window as any).getRecaptchaAction = undefined;
            (window as any).executeRecaptcha = undefined;
        };
    }, [setIsVerified, captchaName, action, enabled]);
};

export default useCaptchaEnterprise;